import React from 'react';
import { useHistory } from 'react-router-dom';
import { LayoutContext, AuthContext, ListingLabelContext } from 'contexts';
import { isEqual } from 'lodash';
import { capitalize, formatTimeStr, formatDateStrWithYearShort } from 'utils';
import { BuildJsonType } from '@bb/domain/dist/typeHelpers';
import type { IUserGetTransfersResponse as TransfersDTO } from '@/app-package';
import {
  TransferState,
  TransferType,
  ITransferOriginalUser as OriginalUser,
} from '@bb/users';
import {
  Space,
  Avatar,
  Button,
  Icon,
  Dropdown,
  Menu,
  MenuItem,
  TableProps,
  Tooltip,
} from 'components';
import { useTableResizer } from 'hooks';

import { StyledTable } from '../../../components';
import { addDatacy, addDatacyToHeader } from '../util';
import ManageUsersContext from '../ManageUsersContext';
import ManageUsersPagination from './ManageUsersPagination';
import { StyledTableCell } from './StyledComponents';
import UserHistoryModal, {
  UserHistoryModalType,
  UserTab,
} from '../UserHistoryModal';
import {
  processTransferStateName,
  TransferStatusTag,
  transferTypeTagMap,
} from './TransferTags';

const TransferStateFilters = [
  TransferState.Pending,
  TransferState.Active,
  TransferState.Cancelled,
  TransferState.Denied,
  // Dismissed is intentionally left out
];

const processTransferTypeName = (type: TransferType) => {
  if (type === TransferType.New) {
    return 'New Agent';
  }
  return capitalize(type);
};

// Determine if transfer is ongoing (not resolved)
// Resolved transfer rows are grayed out
const isTransferOngoing = (transfer: TransfersDTO['transfers'][0]) =>
  transfer.state === TransferState.Pending;

const getRedirectLink = (transfer: TransfersDTO['transfers'][0]) => {
  if (transfer.type === TransferType.Incoming)
    return `transfer/incoming/${transfer.id}`;
  if (transfer.type === TransferType.Outgoing)
    return `edit/${(transfer.user as BuildJsonType<OriginalUser>).id}`;
  if (transfer.type === TransferType.New) return `transfer/new/${transfer.id}`;
  return 'new';
};

const getTransferToolTipContent = (transfer: TransfersDTO['transfers'][0]) => {
  if (transfer.state === TransferState.Pending) {
    if (transfer.type === TransferType.Incoming)
      return 'Your brokerage has requested that this agent be transferred from another brokerage.';
    if (transfer.type === TransferType.Outgoing)
      return 'Another brokerage is requesting that this agent be transferred to them.';
  }
  return '';
};

const TransfersTable: React.FC = () => {
  const history = useHistory();
  const { transition } = React.useContext(LayoutContext);
  const { auth } = React.useContext(AuthContext);
  const { getBoardName } = React.useContext(ListingLabelContext);
  const {
    isTransfersLoading: isLoading,
    transfers,
    transferCount,
    transferStateFilters,
    transferTypeFilters,
    transfersSortControls: sortControls,
    setTransferStateFilters,
    setTransferTypeFilters,
    setTransfersSortControls: setSortControls,
  } = React.useContext(ManageUsersContext);

  const tableWrapperRef = React.useRef<HTMLDivElement>();

  const { tableHeight, tableHeaderHeight } = useTableResizer({
    wrapperRef: tableWrapperRef,
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Controls visibility of transfer history modal
  const [selectedTransferForHistory, setSelectedTransferForHistory] =
    React.useState<TransfersDTO['transfers'][number]>();

  const { sort, isDesc } = sortControls;

  const columns: TableProps['columns'] = [
    {
      width: 250,
      title: 'Name',
      key: 'name',
      dataIndex: 'user',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'name'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      render: (
        user: TransfersDTO['transfers'][0]['user'],
        transfer: TransfersDTO['transfers'][0],
      ) => (
        <StyledTableCell $active={isTransferOngoing(transfer)}>
          <Space align="center" size="middle">
            <Avatar user={user} />
            {`${user.firstName} ${user.lastName}`}
          </Space>
        </StyledTableCell>
      ),
      onCell: addDatacy('transfers-name'),
      onHeaderCell: addDatacyToHeader('transfers-name'),
    },
    {
      width: 120,
      title: 'Status',
      key: 'status',
      dataIndex: 'state',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'status'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      filteredValue: transferStateFilters,
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={transferStateFilters.length ? 'primary' : undefined}
        />
      ),
      filters: TransferStateFilters.map(state => ({
        text: processTransferStateName(state),
        value: state,
      })),
      render: (
        state: TransferState,
        transfer: TransfersDTO['transfers'][0],
      ) => {
        const tooltipContent = getTransferToolTipContent(transfer);
        const tag = <TransferStatusTag state={state} />;
        return (
          <StyledTableCell $active={isTransferOngoing(transfer)}>
            {tooltipContent ? (
              <Tooltip
                title={tooltipContent}
                destroyTooltipOnHide
                mouseEnterDelay={0.25}
              >
                <div>{tag}</div>
              </Tooltip>
            ) : (
              tag
            )}
          </StyledTableCell>
        );
      },
      onCell: addDatacy('transfers-status'),
      onHeaderCell: addDatacyToHeader('transfers-status'),
    },
    {
      width: 200,
      title: 'Type',
      key: 'type',
      dataIndex: 'type',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'type'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      filteredValue: transferTypeFilters,
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={transferTypeFilters.length ? 'primary' : undefined}
        />
      ),
      filters: Object.values(TransferType).map(type => ({
        text: processTransferTypeName(type),
        value: type,
      })),
      render: (type: TransferType, transfer: TransfersDTO['transfers'][0]) => (
        <StyledTableCell $active={isTransferOngoing(transfer)}>
          {transferTypeTagMap[type]}
        </StyledTableCell>
      ),
      onCell: addDatacy('transfers-type'),
      onHeaderCell: addDatacyToHeader('transfers-type'),
    },
    {
      width: 200,
      title: 'Brokerage',
      key: 'brokerage',
      dataIndex: 'brokerage',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'brokerage'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      render: (value: string, transfer: TransfersDTO['transfers'][0]) => (
        <StyledTableCell $active={isTransferOngoing(transfer)}>
          {value}
        </StyledTableCell>
      ),
      onCell: addDatacy('transfers-brokerage'),
      onHeaderCell: addDatacyToHeader('transfers-brokerage'),
    },
    {
      width: '20ch',
      title: 'Member ID',
      key: 'memberIds',
      dataIndex: 'user',
      render: (
        user: TransfersDTO['transfers'][0]['user'],
        transfer: TransfersDTO['transfers'][0],
      ) => (
        <StyledTableCell $active={isTransferOngoing(transfer)}>
          {user.mls_numbers
            .map(({ board, number }) => `${getBoardName(board)}: ${number}`)
            .join(', ')}
        </StyledTableCell>
      ),
      onCell: addDatacy('transfers-memberid'),
      onHeaderCell: addDatacyToHeader('transfers-memberid'),
    },
    {
      width: 175,
      title: 'Email',
      key: 'email',
      dataIndex: 'user',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'email'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      render: (
        user: TransfersDTO['transfers'][0]['user'],
        transfer: TransfersDTO['transfers'][0],
      ) => (
        <StyledTableCell $active={isTransferOngoing(transfer)}>
          {user.email}
        </StyledTableCell>
      ),
      onCell: addDatacy('transfers-email'),
      onHeaderCell: addDatacyToHeader('transfers-email'),
    },
    {
      width: 240,
      title: 'Date',
      key: 'lastStateChange',
      dataIndex: 'lastStateChange',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'lastStateChange'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      render: (value, transfer: TransfersDTO['transfers'][0]) => (
        <StyledTableCell $active={isTransferOngoing(transfer)}>
          {`${formatDateStrWithYearShort(value)} @ ${formatTimeStr(value)}`}
        </StyledTableCell>
      ),
      onCell: addDatacy('transfers-date'),
      onHeaderCell: addDatacyToHeader('transfers-date'),
    },
    {
      width: 80,
      title: '',
      key: 'actions',
      dataIndex: '',
      align: 'center',
      render: (transfer: TransfersDTO['transfers'][0]) => (
        <Dropdown
          placement="topLeft"
          trigger={['click']}
          getPopupContainer={node => node.parentElement}
          dropdownRender={() => (
            <div
              onClick={e => e.stopPropagation()}
              data-cy="transfers-dropdown"
            >
              <Menu>
                {isTransferOngoing(transfer) && (
                  <MenuItem
                    key={'manage'}
                    onClick={() => history.push(getRedirectLink(transfer))}
                    data-cy="transfers-dropdown-manage"
                  >
                    Manage
                  </MenuItem>
                )}
                <MenuItem
                  key={'history'}
                  onClick={() => {
                    setSelectedTransferForHistory(transfer);
                  }}
                  data-cy="transfers-dropdown-history"
                >
                  History
                </MenuItem>
              </Menu>
            </div>
          )}
        >
          <Button type="text" size="small" onClick={e => e.stopPropagation()}>
            <Icon type="ellipsis" width={25} />
          </Button>
        </Dropdown>
      ),
      onCell: addDatacy('transfers-options'),
    },
  ];

  const dataSource =
    auth &&
    transfers.map(transfer => {
      const { id, user, state, type, lastStateChange } = transfer;
      return {
        id,
        key: id,
        user,
        state,
        type,
        brokerage:
          transfer.type === TransferType.New
            ? ''
            : transfer.otherOrgDetails.name,
        lastStateChange,
      };
    });

  return (
    <>
      <div ref={tableWrapperRef}>
        <StyledTable
          style={{ transition: `all ${transition}s` }}
          $height={tableHeight}
          $headerHeight={tableHeaderHeight}
          tableLayout="fixed"
          pagination={false}
          showSorterTooltip={false}
          loading={isLoading}
          dataSource={dataSource}
          columns={columns}
          scroll={{ x: '100%', y: `calc(100% - ${tableHeaderHeight}px)` }}
          onRow={(transfer: TransfersDTO['transfers'][0]) => {
            const isOngoing = isTransferOngoing(transfer);
            if (isOngoing)
              return { onClick: () => history.push(getRedirectLink(transfer)) };
            // Provides some accessibility but is also used as a css selector to gray out the row
            return { 'aria-disabled': true };
          }}
          onChange={(
            pagination,
            filters,
            sorter: { columnKey: any; order: any },
          ) => {
            const status = filters.status || [];
            const type = filters.type || [];

            if (!isEqual(status, transferStateFilters)) {
              setTransferStateFilters(status as TransferState[]);
            }
            if (!isEqual(type, transferTypeFilters)) {
              setTransferTypeFilters(type as TransferType[]);
            }

            if (sorter.order) {
              const newControls = {
                sort: sorter.columnKey,
                isDesc: sorter.order === 'descend',
              };
              if (!isEqual(sortControls, newControls)) {
                setSortControls(newControls);
              }
            } else {
              setSortControls({ sort: '', isDesc: false });
            }
          }}
          data-cy="transfers-table"
        />
        <ManageUsersPagination count={transferCount} tableName="transfers" />
      </div>
      <UserHistoryModal
        type={UserHistoryModalType.TRANSFER}
        isModalVisible={!!selectedTransferForHistory}
        transfer={selectedTransferForHistory}
        handleCancel={() => setSelectedTransferForHistory(undefined)}
        tabsToDisplay={[UserTab.TRANSFER]}
      />
    </>
  );
};

export default TransfersTable;
